<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import ChevronDown from '/svg/chevron-down.svg'
import { isoToFaName } from '../constants/iso-to-fa-name.constant'

interface CountryOption {
  label: string
  value: string
  flagUrl?: string
  iso?: string
}

interface Props {
  name: string
  label?: string
  placeholder?: string
  disabled?: boolean
  modelValue?: string | number
  countryValue?: string | number
  error?: string
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  placeholder: '',
  modelValue: '',
  countryValue: '',
  error: '',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void
  (e: 'update:countryValue', value: string | number): void
}>()

const { countries } = useGeneralSettings()

const wrapper = ref<HTMLElement | null>(null)
const isFocused = ref(false)
const isOpen = ref(false)
const searchQuery = ref('')

// computed local binding for the input (so we don't v-model a prop directly)
const inputValue = computed<string>({
  get() {
    return (props.modelValue ?? '') as string
  },
  set(v: string) {
    emit('update:modelValue', v)
  },
})

// computed for selected country (getter/setter emits update)
const selectedCountryComputed = computed<string | number>({
  get() {
    return props.countryValue ?? ''
  },
  set(v: string | number) {
    emit('update:countryValue', v)
  },
})

const selectedCountry = ref<CountryOption | undefined>(undefined)

const getFaName = (country: CountryOption) => (country.iso && isoToFaName[country.iso]) || country.label || 'نامشخص'

const filteredCountries = computed(() => {
  const list = countries.value || []
  if (!searchQuery.value) return list
  const q = searchQuery.value.toLowerCase()
  return list.filter(
    (c) =>
      (c.label && c.label.toLowerCase().includes(q)) ||
      (c.iso && c.iso.toLowerCase().includes(q)) ||
      (getFaName(c) && getFaName(c).includes(searchQuery.value))
  )
})

function updateCountry(value: string | number) {
  selectedCountryComputed.value = value
  selectedCountry.value = (countries.value || []).find((c) => c.value === value)
  isOpen.value = false
  searchQuery.value = ''
}

function toggleOpen() {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    setTimeout(() => {
      const el = document.getElementById('country-search')
      el?.focus()
    }, 100)
  } else {
    searchQuery.value = ''
  }
}

function handleClickOutside(e: MouseEvent) {
  if (wrapper.value && !wrapper.value.contains(e.target as Node)) {
    isOpen.value = false
  }
}

watch(
  () => props.countryValue,
  (v) => {
    selectedCountry.value = (countries.value || []).find((c) => c.value === v)
  },
  { immediate: true }
)

watch(
  () => countries.value,
  () => {
    selectedCountry.value = (countries.value || []).find((c) => c.value === props.countryValue)
  },
  { immediate: true }
)

onMounted(() => document.addEventListener('click', handleClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside))
</script>

<template>
    <div class="relative flex flex-col gap-1 w-full " ref="wrapper">
        <!-- Label -->
        <label v-if="label"
            class="w-fit bg-white px-1 transition-all duration-400 z-50 text-[#1b325a] pointer-events-none"
            :class="(isFocused || inputValue) ? 'absolute ms-4 -mt-2 text-[10px] font-bold' : 'absolute mt-3.5 ms-2 text-[12px] font-medium'">
            {{ label }}
        </label>

        <!-- Input + Country select container -->
        <div class="relative w-full flex items-center rounded-xl border h-12 border-[#d5ddea] bg-white focus-within:border-black transition"
            :class="error ? 'border-[#ed145b]' : ''">
            <!-- Input on the START (left) -->
            <input :name="name" v-model="inputValue" :placeholder="placeholder" :disabled="disabled"
                class="w-[80%] px-4 text-[13px] font-medium text-[#1b325a] outline-none bg-transparent"
                @focus="isFocused = true" @blur="isFocused = false" />

            <!-- Divider between input and country -->
            <div class="h-6 border-start border-l border-[#ebeef3] mx-2"></div>

            <!-- Country select on the END (right) -->
            <div class="relative h-full pe-5 flex items-center">
                <div class="cursor-pointer flex items-center gap-0 select-none" @click.stop="toggleOpen">
                    <img :src="ChevronDown" class="w-4 h-4 transition-transform duration-200"
                        :class="{ 'rotate-180': isOpen }" />
                    <img v-if="selectedCountry?.flagUrl" :src="selectedCountry.flagUrl"
                        class="w-6 h-4 rounded object-cover" />
                    <span v-else class="text-[#1b325a] text-sm">🌐</span>

                </div>

                <!-- Dropdown -->
                <div v-if="isOpen"
                    class="absolute top-full end-0 mt-2 bg-white border border-[#d5ddea] rounded-xl shadow-lg z-[999] w-56 max-h-60 overflow-y-auto custom-dropdown">
                    <div class="sticky top-0 bg-white p-2 border-b border-[#d5ddea]">
                        <input id="country-search" v-model="searchQuery" placeholder="جستجوی کشور..."
                            class="w-full px-2 py-1 text-sm border border-[#d5ddea] rounded-lg outline-none focus:ring-1 focus:ring-[#1b325a]" />
                    </div>

                    <div v-if="filteredCountries.length > 0">
                        <div v-for="c in filteredCountries" :key="c.value" @click="updateCountry(c.value)"
                            class="px-3 py-2 text-sm cursor-pointer flex items-center gap-2 hover:bg-[#f8f9fa] transition-all"
                            :class="c.value === selectedCountryComputed ? 'bg-[#DAE9F8] font-bold text-[#1b325a]' : 'text-[#1b325a]'">
                            <img v-if="c.flagUrl" :src="c.flagUrl" class="w-6 h-4 rounded object-cover" />
                            <span>{{ getFaName(c) }}</span>
                        </div>
                    </div>

                    <div v-else class="px-3 py-2 text-center text-gray-500 text-sm">کشوری یافت نشد</div>
                </div>
            </div>
        </div>

        <!-- Error -->
        <div v-if="error" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
            <img src="/svg/error-icon.svg" width="16" alt="error" />
            {{ error }}
        </div>
    </div>
</template>

<style scoped>
.custom-dropdown::-webkit-scrollbar {
    width: 6px;
}

.custom-dropdown::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style>
